/*
 * SCROLL PAGER — a big, obvious page-down / page-up bar on every scrolling surface.
 *
 * Owner direction 2026-07-27: "apparently for some people the concept of scrolling is
 * still difficult to grasp, especially [as] the scroll bar is quite thin". So each
 * scrolling surface (the centre tabs' content box, the word-list accordion, an open
 * list's words) gets two floating bars — one at its top edge, one at its bottom — each
 * showing ONLY when there is something that way to scroll to, so an unscrollable
 * surface looks exactly as before.
 *
 * The bar spans the surface, but ONLY THE CHEVRONS IN THE MIDDLE OF IT ARE THE CONTROL:
 * the bar itself is pointer-events:none, so the strip either side neither reacts to the
 * pointer nor swallows a click meant for the row underneath. The full width still earns
 * its keep — it is the band that WAKES the chevrons as the pointer approaches that edge,
 * which is measured from pointer coordinates rather than hit-testing.
 *
 * TWO GEARS, owner spec 2026-07-27 (second pass) — the pointer chooses, nothing else:
 *   - HOVER the chevrons and they start creeping ITEM BY ITEM (rows, not screenfuls),
 *     slowly at first and picking up to five times that pace — a reader can watch the
 *     rows go by without pressing anything;
 *   - PRESS them for a page at once (a screenful less a sliver of overlap, so a line of
 *     context survives) — that is what a click is — and HOLD for a continuous glide at
 *     that same pace, accelerating to three times it. The glide is frame-by-frame rather
 *     than a chain of jumps, so holding these reads like holding a scrollbar arrow.
 * The fast end of the hover gear stays strictly SLOWER than the slow end of the page
 * gear — enforced in px/s, not just in timer ticks, because a row can be a whole
 * multi-sense card. The gears must never overlap: that is what makes the two feel like
 * different tools rather than one control with a mood.
 *
 * AND A RESTING BAR GETS OUT OF THE WAY (owner direction 2026-08-07): while the surface is
 * standing still, bare chevrons lying on ink — a word, an icon, a card's border — fade out
 * altogether, and come back the moment anything scrolls or the pointer nears that edge. See
 * the veil section for what counts as ink and why a flat fill does not.
 *
 * "Item" needs no per-surface configuration: the pager walks the scroll container's
 * element children, or — when the container holds a single wrapper (#defId, #learnId)
 * — that wrapper's children. That is the row list on every surface we attach to. Where
 * a surface has no rows at all it falls back to a fixed pixel step.
 *
 * THE BARS ARE BODY-MOUNTED AND position:fixed, pinned to the container's live client
 * box — not children of the scrolled element. Every one of these surfaces has its
 * content wiped and re-rendered (`.empty()`) many times per session, and a child would
 * be swept away with it; a sibling overlay outlives every repaint, so attach() is
 * called ONCE per container and never again. The same choice escapes the accordion's
 * overflow clip. A 250ms tracker (plus the container's own scroll event and window
 * resize) keeps the pair on the box and re-decides which of them is warranted; a
 * container that leaves the DOM detaches itself on the next tick.
 *
 * Attach sites: this file's boot pass for the three static surfaces, and main.js where
 * a word-list section body mints its .wlWordsClass (the one place it is created).
 */
var SCROLL_PAGER;

(function () {
	"use strict";

	var TRACK_MS = 250;        // re-pin / re-decide cadence while any pager is attached
	var HOVER_INTENT_MS = 300; // a pointer merely crossing a bar must not set it walking
	var HOVER_START_MS = 550;  // the hover gear's opening item step…
	var HOVER_TOP = 5;         // …and the most it may speed up (spec: five times)
	var HOVER_ACCEL = 0.86;    // each item step waits this fraction of the one before
	var HOVER_CEILING = 0.7;   // fastest hover pace, as a fraction of the SLOWEST page pace
	var PAGE_START_MS = 800;   // the page gear's opening pace: one screenful per this…
	var PAGE_TOP = 3;          // …and the most it may speed up (spec: three times)
	var PAGE_RAMP_MS = 2500;   // how long the held glide takes to reach that top pace
	var PAGE_HOLD_MS = 420;    // a press longer than this turns the page into a held glide
	var PAGE_KEEP = 0.12;      // a page keeps this much of the old view on screen
	var FALLBACK_STEP = 28;    // px per step on a surface with no item rows to walk
	var EDGE_EPS = 2;          // px of slack before "at the very top/bottom" stops counting
	var GONE_TICKS = 8;        // ticks a container may stay off the page before its pager goes
	var NEAR_IN = 110;         // px INTO the surface within which the pointer wakes a bar…
	var NEAR_OUT = 30;         // …and px past its edge, the other way
	var NEAR_ACTIVE_MS = 3000; // a wake from PROXIMITY alone lasts this long, then sleeps even
	                           // if the pointer never left the vicinity — only a real hover on
	                           // the control itself (the pill) holds it awake past that
	var STILL_MS = 220;        // no scroll for this long = the surface is standing still
	var INK_PAD = 3;           // px of clearance the chevrons want around them before they
	                           // count as sitting ON something
	var STICKY_SLACK = 4;      // px a .plStickyHeadClass may sit below the surface's top edge
	                           // and still count as parked there (the surface's own padding)

	var pagers = [];
	var tracker = null;

	// ── geometry ────────────────────────────────────────────────────────────────

	// The container's CONTENT box in viewport coordinates: client* excludes both the
	// border and the vertical scrollbar, so a bar sized from it never covers the bar
	// the mouse-comfortable user may still be reaching for.
	function contentBox(el) {
		var rects = el.getClientRects();
		if (!rects.length) { return null; }
		var r = rects[0];
		return {
			left: r.left + el.clientLeft,
			top: r.top + el.clientTop,
			width: el.clientWidth,
			height: el.clientHeight
		};
	}

	function elementChildren(node) {
		var out = [];
		var kids = node ? node.children : null;
		for (var i = 0; kids && i < kids.length; i++) { out.push(kids[i]); }
		return out;
	}

	// Every item row's top, as a scrollTop value that would put that row at the top of
	// the view. Measured once when a hold starts (a walk over hundreds of rows must not
	// re-measure the whole list on every step) and re-measured when the walk runs off
	// the end — which is how an endless-scroll list's freshly appended rows join in.
	function itemTops(el) {
		var kids = elementChildren(el);
		if (kids.length === 1) { kids = elementChildren(kids[0]); }
		var box = contentBox(el);
		if (!box) { return []; }
		var base = box.top - el.scrollTop;
		var tops = [];
		for (var i = 0; i < kids.length; i++) {
			var r = kids[i].getBoundingClientRect();
			if (!r.height) { continue; }   // collapsed / display:none children are not rows
			tops.push(Math.round(r.top - base));
		}
		tops.sort(function (a, b) { return a - b; });
		return tops;
	}

	// A STICKY HEADER PARKED AT THE TOP EDGE OWNS THAT STRIP, and the up bar must start below
	// it. The two want the same pixels by construction: the bar pins to the surface's top edge,
	// which is exactly where a sticky header stops. The bar itself lets the pointer through,
	// but the chevrons in its middle do NOT — so they land on the header's own controls and
	// swallow clicks meant for them. That is not theoretical: My Garden's band heads fold on a
	// click, and with the chevrons sitting on "NEEDS WATERING" a click there paged the list up
	// instead of folding the band, which read as a header that had stopped responding.
	//
	// A sticky header announces itself with .plStickyHeadClass rather than being discovered:
	// these lists run to hundreds of rows, this is re-decided on a timer, and asking the
	// computed style of an unknown number of elements to find `position: sticky` is exactly
	// the kind of work that cannot go on a timer. The marker keeps the cost at one
	// querySelectorAll and a handful of rects — and at zero on a surface that has no headers.
	function stickyTopInset(p, box) {
		var heads = p.el.querySelectorAll(".plStickyHeadClass");
		var best = 0;
		for (var i = 0; i < heads.length; i++) {
			var r = heads[i].getBoundingClientRect();
			// The one PARKED at the edge right now: pinned to the top (with a couple of px of
			// slack for the surface's own padding) and still standing on it. Headers further
			// down the list are in normal flow and in nobody's way.
			if (r.height && r.top <= box.top + STICKY_SLACK && r.bottom > box.top) {
				best = Math.max(best, r.bottom - box.top);
			}
		}
		// Half the surface is the sanity bound: a "header" taller than that is not one, and
		// pushing the bar off the box would lose it altogether.
		return Math.min(best, box.height / 2);
	}

	// THE SAME RULE AT THE BOTTOM EDGE: a strip docked over the surface's foot (the dictionary's
	// collocation strip) owns those pixels, and the down bar must end above it — otherwise the
	// chevrons sit exactly on the strip and swallow the click that was meant to unfold it. The
	// dock is a SIBLING of the scroll container (it must survive the container's re-renders), so
	// the marker is looked for beside the surface, not inside it; only one parked on the bottom
	// edge right now counts.
	function dockedFootInset(p, box) {
		if (!p.el.parentElement) { return 0; }
		var feet = p.el.parentElement.querySelectorAll(".plDockedFootClass");
		var bottom = box.top + box.height;
		var best = 0;
		for (var i = 0; i < feet.length; i++) {
			var r = feet[i].getBoundingClientRect();
			if (r.height && r.top < bottom && r.bottom >= bottom - STICKY_SLACK) {
				best = Math.max(best, bottom - r.top);
			}
		}
		// The sanity bound clears the dock's own max-height (60% of the surface when its panel is
		// unfolded) — capping at half like the sticky heads would park the chevrons INSIDE the open
		// panel instead of on its top edge.
		return Math.min(best, box.height * 0.7);
	}

	// ── painting ────────────────────────────────────────────────────────────────

	function paint(p) {
		var el = p.el;
		// Off the page: hide, and reclaim only after it has STAYED off for a while. A
		// container is often attached a moment after its pager (a word-list section body is
		// built whole and appended after), and a container that is merely between renders
		// must keep its pager — so only a surface that is still gone seconds later is dead.
		if (!el.isConnected) {
			p.gone = (p.gone || 0) + 1;
			vis(p.up, false);
			vis(p.down, false);
			if (p.gone > GONE_TICKS) { detach(p); }
			return;
		}
		p.gone = 0;
		var box = contentBox(el);
		p.box = box;   // the proximity test reads this — never the live DOM (see nearPointer)
		var max = el.scrollHeight - el.clientHeight;
		if (!box || max <= 2 * EDGE_EPS) { vis(p.up, false); vis(p.down, false); return; }
		var canUp = el.scrollTop > EDGE_EPS;
		var canDown = el.scrollTop < max - EDGE_EPS;
		vis(p.up, canUp);
		vis(p.down, canDown);
		// Placed only while shown: a hidden bar has no height to subtract, and where it
		// would have gone does not matter.
		if (canUp) { place(p.up, box, -1, p.upInset || 0); }
		if (canDown) { place(p.down, box, 1, dockedFootInset(p, box)); }
	}

	// Each bar is anchored by the edge it belongs to — the top bar by its top, the bottom bar
	// by its BOTTOM (not top-minus-height). A bar changes height as the pointer nears it, and
	// anchoring the bottom one by a height read would slide its edge off the surface for the
	// length of that transition; growth has to happen inward, away from the edge.
	function place(bar, box, dir, inset) {
		bar.style.left = box.left + "px";
		bar.style.width = box.width + "px";
		if (dir < 0) {
			bar.style.top = (box.top + (inset || 0)) + "px";
			bar.style.bottom = "auto";
		} else {
			bar.style.top = "auto";
			bar.style.bottom = (window.innerHeight - (box.top + box.height - (inset || 0))) + "px";
		}
	}

	function vis(bar, on) {
		bar.style.display = on ? "flex" : "none";
	}

	function paintAll() {
		for (var i = pagers.length - 1; i >= 0; i--) { paint(pagers[i]); }
	}

	// ── proximity: a bar at rest is thin and half-there, and wakes when aimed at ──
	//
	// Owner direction 2026-07-27 (second pass): the first cut washed the full width of the
	// surface with a gradient, which read as the top of the page being blurred. The wash is
	// gone; instead each bar rests small and translucent — visible enough to say "there is
	// more this way", quiet enough not to sit ON the reading — and comes to full size and
	// full opacity when the pointer is anywhere near its edge of the surface.
	//
	// Measured against the stored container box (paint keeps it current), NOT against live
	// rects: this runs on every pointermove, and it must never read layout.
	function nearPointer(x, y) {
		for (var i = 0; i < pagers.length; i++) {
			var p = pagers[i];
			var b = p.box;
			if (!b) { continue; }
			var inX = x >= b.left - NEAR_OUT && x <= b.left + b.width + NEAR_OUT;
			var bottom = b.top + b.height;
			// Asymmetric on purpose: the band reaches well INTO the surface (that is where a
			// reader's pointer travels) and only a little past its edge.
			wake(p.up, inX && y >= b.top - NEAR_OUT && y <= b.top + NEAR_IN);
			wake(p.down, inX && y >= bottom - NEAR_IN && y <= bottom + NEAR_OUT);
		}
	}

	// Waking is edge-triggered, not level-triggered: a bar activates when the pointer
	// ENTERS the vicinity, then sleeps on its own after NEAR_ACTIVE_MS even though the
	// pointer never left — re-entering the same vicinity without a fresh approach must
	// not re-arm it, or "activates for 3s" would never actually end. Hovering the
	// control itself (the pill) is the one thing that holds it awake past that; see the
	// pointerenter/pointerleave pair in bind().
	function wake(bar, on) {
		var wasNear = !!bar.plWasNear;
		bar.plWasNear = !!on;
		if (!on) {
			bar.classList.remove("plScrollPagerNearClass");
			clearTimeout(bar.plSleepTimer);
			bar.plSleepTimer = null;
			// ONLY on the transition out of the vicinity. This branch runs on every single
			// pointermove, and the ink test reads layout — re-deciding here every move would
			// undo exactly what this function's "never read layout" rule protects.
			if (wasNear) { reveil(bar); }
			return;
		}
		if (wasNear) { return; }   // still in the same approach — no re-arming
		bar.classList.add("plScrollPagerNearClass");
		setVeil(bar, false);       // accented always shows, whatever is under it
		if (!bar.plOverControl) { armSleep(bar); }
	}

	// Starts (or restarts) the countdown to sleep. If the pointer is directly over the
	// control when it fires, it defers to the pointerleave handler instead of sleeping
	// out from under an in-progress hover/press.
	function armSleep(bar) {
		clearTimeout(bar.plSleepTimer);
		bar.plSleepTimer = setTimeout(function () {
			bar.plSleepTimer = null;
			if (bar.plOverControl) { return; }
			bar.classList.remove("plScrollPagerNearClass");
			reveil(bar);
		}, NEAR_ACTIVE_MS);
	}

	document.addEventListener("pointermove", function (e) {
		nearPointer(e.clientX, e.clientY);
	}, { passive: true });

	// The pointer leaving the window can't be caught by a move — every bar goes back to rest.
	document.addEventListener("pointerleave", function () {
		nearPointer(-1e6, -1e6);
	}, { passive: true });

	// ── the veil: resting chevrons get out of the way of the reading ────────────
	//
	// Owner direction 2026-08-07. At rest the chevrons are bare glyphs laid straight over the
	// surface, and where they land on a word — or an icon, or a card's orange edge — the two
	// read as one smudge. So a resting bar FADES OUT COMPLETELY while both of these hold:
	//
	//   1. the surface is standing still (nothing has scrolled for STILL_MS) — while rows are
	//      moving the chevrons are the thing that explains the movement, and they stay;
	//   2. the glyphs overlap ink.
	//
	// Never when the bar is ACCENTED — the plate-and-orange-border state the pointer's approach
	// puts it in is the state you are aiming at, and it shows over anything.
	//
	// "Ink" is deliberately narrower than "content": text lines, icons, images, form controls
	// and visible BORDERS — the marks a chevron would tangle with — but NOT a flat background
	// fill. Every row is a filled box; counting fills would veil the chevrons permanently and
	// throw away the "there is more this way" hint they exist to give.

	var GRAPHIC_TAGS = { IMG: 1, SVG: 1, CANVAS: 1, VIDEO: 1, INPUT: 1, SELECT: 1, TEXTAREA: 1, HR: 1 };

	function hits(r, t) {
		return r.left < t.right && r.right > t.left && r.top < t.bottom && r.bottom > t.top;
	}

	// A border side counts only if it is both wide and actually painted, and only its own
	// band does — a bordered row is mostly empty space, and the chevrons may sit in it.
	// The alpha must be read from the FOURTH component, not from "ends in 0" — the app's own
	// orange is rgb(255, 165, 0), and a blue channel of zero is not transparency.
	function edgeWidth(width, color) {
		var w = parseFloat(width) || 0;
		if (!w) { return 0; }
		if (color === "transparent") { return 0; }
		var m = /^rgba\([^,]+,[^,]+,[^,]+,\s*([\d.]+)\s*\)$/.exec(color);
		if (m && parseFloat(m[1]) === 0) { return 0; }
		return w;
	}

	function borderHits(r, cs, t) {
		var w = edgeWidth(cs.borderTopWidth, cs.borderTopColor);
		if (w && hits({ left: r.left, right: r.right, top: r.top, bottom: r.top + w }, t)) { return true; }
		w = edgeWidth(cs.borderBottomWidth, cs.borderBottomColor);
		if (w && hits({ left: r.left, right: r.right, top: r.bottom - w, bottom: r.bottom }, t)) { return true; }
		w = edgeWidth(cs.borderLeftWidth, cs.borderLeftColor);
		if (w && hits({ left: r.left, right: r.left + w, top: r.top, bottom: r.bottom }, t)) { return true; }
		w = edgeWidth(cs.borderRightWidth, cs.borderRightColor);
		if (w && hits({ left: r.right - w, right: r.right, top: r.top, bottom: r.bottom }, t)) { return true; }
		return false;
	}

	// Text is measured by RANGE rects, not by its element's box: a row's box spans the whole
	// width while its label may be six characters at one end, and the chevrons sitting in the
	// empty half of that row are not on anything.
	function textHits(node, t) {
		if (!/\S/.test(node.nodeValue)) { return false; }
		var range = document.createRange();
		range.selectNodeContents(node);
		var rs = range.getClientRects();
		for (var i = 0; i < rs.length; i++) {
			if (rs[i].width && rs[i].height && hits(rs[i], t)) { return true; }
		}
		return false;
	}

	function inkIn(node, t) {
		var r = node.getBoundingClientRect();
		if (!r.width || !r.height || !hits(r, t)) { return false; }
		if (GRAPHIC_TAGS[node.tagName] || node.classList.contains("pl-ic")) { return true; }
		var cs = window.getComputedStyle(node);
		if (cs.visibility === "hidden" || cs.opacity === "0") { return false; }
		// A masked or image-painted box IS its picture — the app's inline icons are exactly this.
		if (cs.backgroundImage !== "none") { return true; }
		if ((cs.maskImage || cs.webkitMaskImage || "none") !== "none") { return true; }
		if (borderHits(r, cs, t)) { return true; }
		for (var n = node.firstChild; n; n = n.nextSibling) {
			if (n.nodeType === 3) { if (textHits(n, t)) { return true; } }
			else if (n.nodeType === 1 && inkIn(n, t)) { return true; }
		}
		return false;
	}

	// The rows the glyphs are lying on, found by hit-testing three points rather than by
	// walking the list: these surfaces carry hundreds of rows and this runs on every tracker
	// tick. Sampling is VERTICAL only — every row on every one of these surfaces is a
	// full-width band, so a column down the middle of the glyphs finds each one it can touch.
	// The precise ink test then happens inside those rows alone.
	function rowsUnder(p, t) {
		var wrap = p.el;
		var kids = elementChildren(wrap);
		if (kids.length === 1) { wrap = kids[0]; }   // the single-wrapper surfaces (#defId, #learnId)
		var midX = (t.left + t.right) / 2;
		var pts = [[midX, t.top + 1], [midX, (t.top + t.bottom) / 2], [midX, t.bottom - 1]];
		var out = [];
		for (var i = 0; i < pts.length; i++) {
			var stack = document.elementsFromPoint(pts[i][0], pts[i][1]);
			for (var j = 0; j < stack.length; j++) {
				var n = stack[j];
				if (n.closest && n.closest(".plScrollPagerClass")) { continue; }   // our own bars
				if (!p.el.contains(n)) { break; }        // nothing of this surface at this point
				while (n && n.parentNode !== wrap) { n = n.parentNode; }
				if (n && out.indexOf(n) < 0) { out.push(n); }
				break;
			}
		}
		return out;
	}

	function overlapsInk(p, bar) {
		// The glyphs, not the pill: at rest the pill is invisible padding, and testing its box
		// would veil the chevrons for ink they are nowhere near.
		var glyph = bar.querySelector(".pl-ic") || bar;
		var r = glyph.getBoundingClientRect();
		if (!r.width || !r.height) { return false; }
		var t = { left: r.left - INK_PAD, right: r.right + INK_PAD,
			top: r.top - INK_PAD, bottom: r.bottom + INK_PAD };
		var rows = rowsUnder(p, t);
		for (var i = 0; i < rows.length; i++) {
			if (inkIn(rows[i], t)) { return true; }
		}
		return false;
	}

	function setVeil(bar, on) {
		if (!!bar.plVeiled === !!on) { return; }
		bar.plVeiled = !!on;
		bar.classList.toggle("plScrollPagerVeiledClass", !!on);
	}

	function veilBar(p, bar, now) {
		if (bar.style.display === "none") { return; }                       // not shown at all
		if (bar.classList.contains("plScrollPagerNearClass")) { setVeil(bar, false); return; }
		if (now - (p.movedAt || 0) < STILL_MS) { setVeil(bar, false); return; }
		setVeil(bar, overlapsInk(p, bar));
	}

	// Re-decide for one bar right now — used the moment a bar goes back to sleep, so it does
	// not linger over the text for up to a tracker tick after the pointer has moved on.
	function reveil(bar) {
		var p = bar.plPager;
		if (p) { veilBar(p, bar, performance.now()); }
	}

	function veilAll() {
		var now = performance.now();
		for (var i = 0; i < pagers.length; i++) {
			veilBar(pagers[i], pagers[i].up, now);
			veilBar(pagers[i], pagers[i].down, now);
		}
	}

	// ── scrolling ───────────────────────────────────────────────────────────────

	function pagePx(el) {
		return Math.max(40, el.clientHeight * (1 - PAGE_KEEP));
	}

	// One page. The FIRST one of a press glides (a single click should read as a move, not
	// a jump-cut); the repeats that follow are instant, because a smooth animation still
	// running when the next repeat lands retargets itself and quietly eats part of the page.
	function pageScroll(p, dir, glide) {
		var el = p.el;
		var page = dir * pagePx(el);
		if (glide && el.scrollBy) { el.scrollBy({ top: page, behavior: "smooth" }); }
		else { el.scrollTop += page; }
	}

	// One item step. Returns false when nothing moved — the walk has reached the end and
	// the loop stops there rather than spinning against the edge.
	function stepOnce(p, dir) {
		var el = p.el;
		var before = el.scrollTop;
		var to = nextTop(p.tops, before, dir);
		if (to == null) {
			p.tops = itemTops(el);           // list may have grown under us (endless scroll)
			to = nextTop(p.tops, before, dir);
		}
		el.scrollTop = (to == null) ? before + dir * FALLBACK_STEP : to;
		return el.scrollTop !== before;
	}

	function nextTop(tops, from, dir) {
		var found = null;
		for (var i = 0; i < tops.length; i++) {
			if (dir > 0) {
				if (tops[i] > from + 1) { return tops[i]; }
			} else if (tops[i] < from - 1) {
				found = tops[i];                 // ascending: the last one above is the nearest
			}
		}
		return found;
	}

	// ── the two gears ───────────────────────────────────────────────────────────

	function stopRun(p) {
		clearTimeout(p.intentTimer);
		clearTimeout(p.runTimer);
		if (p.raf) { cancelAnimationFrame(p.raf); }
		p.intentTimer = null;
		p.runTimer = null;
		p.raf = null;
		p.mode = null;
	}

	// Hover gear: item by item, opening slow and ramping to HOVER_TOP× that pace.
	function hoverRun(p) {
		var el = p.el;
		if (!stepOnce(p, p.dir)) { stopRun(p); return; }
		paint(p);
		p.delay = Math.max(HOVER_START_MS / HOVER_TOP, p.delay * HOVER_ACCEL);
		// The ramp is in TICKS, the ceiling is in PIXELS, and the ceiling is sized by the
		// step that is ABOUT TO happen — not the one just taken. A row can be a whole
		// multi-sense card: with a short wait ahead of a tall row, the list lurches past a
		// page's worth in a blink and the two gears stop feeling different. Waiting in
		// proportion to what comes next holds every step at or under the ceiling.
		// The ramp keeps its own value either way, so tall rows don't freeze the
		// acceleration — they only stretch the gap around themselves.
		var maxPxPerMs = HOVER_CEILING * pagePx(el) / PAGE_START_MS;
		var wait = Math.max(p.delay, nextDistance(p) / maxPxPerMs);
		p.runTimer = setTimeout(function () { hoverRun(p); }, wait);
	}

	// How far the next item step would travel (the fallback step where there are no rows).
	function nextDistance(p) {
		var from = p.el.scrollTop;
		var to = nextTop(p.tops, from, p.dir);
		return to == null ? FALLBACK_STEP : Math.abs(to - from);
	}

	// Page gear, held: a CONTINUOUS GLIDE rather than a chain of jumps (owner spec, fourth
	// pass — "same as you press and hold on the vertical scroll bar itself"). Same pace as
	// the repeats it replaces, a screenful per PAGE_START_MS opening and PAGE_TOP× that at
	// the top, only painted frame by frame instead of a screenful at a time.
	//
	// The position is carried as a FLOAT (p.pos) and written to scrollTop each frame: at
	// ~0.85px/ms a frame is well under a pixel at the start, so accumulating in scrollTop
	// itself would round most of the motion away.
	function glideRun(p, now) {
		var el = p.el;
		var dt = Math.min(50, now - p.glideLast);   // a backgrounded tab must not lurch
		p.glideLast = now;
		var ramp = Math.min(1, (now - p.glideFrom) / PAGE_RAMP_MS);
		var v = (pagePx(el) / PAGE_START_MS) * (1 + (PAGE_TOP - 1) * ramp);
		var before = el.scrollTop;
		p.pos += p.dir * v * dt;
		el.scrollTop = p.pos;
		if (Math.abs(el.scrollTop - before) < 0.01 && dt > 0) { stopRun(p); return; }   // hit the end
		p.pos = el.scrollTop;   // re-sync: the element clamps, we must not run off past it
		paint(p);
		p.raf = requestAnimationFrame(function (t) { glideRun(p, t); });
	}

	function startHover(p, dir) {
		stopRun(p);
		p.mode = "hover";
		p.dir = dir;
		p.delay = HOVER_START_MS;
		p.tops = itemTops(p.el);
		hoverRun(p);              // the first row moves as soon as the intent settles
	}

	// A press is one page straight away — that is what a click is, and it must land as a
	// definite move, not as the first 200ms of a glide. Held past PAGE_HOLD_MS (by which
	// time that page has arrived), it becomes the continuous glide.
	function startPage(p, dir) {
		stopRun(p);
		p.mode = "page";
		p.dir = dir;
		pageScroll(p, dir, true);
		p.runTimer = setTimeout(function () {
			p.pos = p.el.scrollTop;
			p.glideFrom = p.glideLast = performance.now();
			p.raf = requestAnimationFrame(function (t) { glideRun(p, t); });
		}, PAGE_HOLD_MS);
	}

	// ── the bars ────────────────────────────────────────────────────────────────

	function makeBar(dir) {
		var b = document.createElement("button");
		b.type = "button";
		// Out of the tab order on purpose: this is a POINTER affordance duplicating what
		// PageUp/PageDown already do for the keyboard, and two extra tab stops per
		// scrolling surface would be a real cost to the people who do use the keyboard.
		b.tabIndex = -1;
		b.className = "plScrollPagerClass "
			+ (dir < 0 ? "plScrollPagerUpClass" : "plScrollPagerDownClass");
		b.style.display = "none";
		// The whole bar is the target; the pill inside it is what makes it legible.
		// NO TOOLTIP (owner spec, second pass): hovering this control now DOES something,
		// so a box explaining it would pop up over the very rows it has started moving.
		// The label survives as aria-label — invisible, and the only thing a tooltip was
		// ever telling a screen reader.
		b.innerHTML = "<span class='plScrollPagerPillClass'>" + plIcon("chevrons-down") + "</span>";
		b.setAttribute("aria-label", getRes(dir < 0 ? "scrollUp_tooltip" : "scrollDown_tooltip"));
		return b;
	}

	// EVERY handler lives on the PILL, not on the bar (owner spec, fourth pass: "only if you
	// hover over the control itself, not in the same horizontal band outside of it"). The bar
	// spans the surface so the chevrons stay centred over it and so the wake band is easy to
	// find — but it is pointer-events:none, so the strip either side of the chevrons neither
	// starts a scroll nor swallows a click meant for the row underneath. Waking on approach
	// is unaffected: that is measured from raw pointer coordinates, not from hit-testing.
	function bind(p, dir) {
		var bar = dir < 0 ? p.up : p.down;
		var pill = bar.querySelector(".plScrollPagerPillClass");
		// Hover = the item gear, after a moment's intent so a pointer crossing the chevrons
		// on its way somewhere else doesn't set the list moving.
		pill.addEventListener("pointerenter", function () {
			// A real hover on the control holds it awake for as long as the pointer stays —
			// the 3s proximity sleep is for the "just passing near the edge" case, not this.
			bar.plOverControl = true;
			clearTimeout(bar.plSleepTimer);
			bar.plSleepTimer = null;
			bar.classList.add("plScrollPagerNearClass");
			setVeil(bar, false);
			if (p.mode) { return; }
			clearTimeout(p.intentTimer);
			p.intentTimer = setTimeout(function () { startHover(p, dir); }, HOVER_INTENT_MS);
		});
		// Press = the page gear. It takes over from the hover walk mid-stride.
		pill.addEventListener("pointerdown", function (e) {
			if (e.button) { return; }
			e.preventDefault();   // no text selection, no focus stolen from the answer field
			startPage(p, dir);
		});
		// Released but still on the chevrons: back to the slow item creep, from the top of
		// its ramp — a second press should feel like a second press, not a continuation.
		pill.addEventListener("pointerup", function () {
			stopRun(p);
			if (pill.matches(":hover")) { startHover(p, dir); }
		});
		pill.addEventListener("pointerleave", function () {
			stopRun(p);
			bar.plOverControl = false;
			// Pointer is presumably still in the vicinity, just off the control now — give it
			// a fresh 3s before sleeping rather than sleeping mid-approach.
			if (bar.plWasNear) { armSleep(bar); }
		});
		pill.addEventListener("pointercancel", function () { stopRun(p); });
		// The chevrons cover a sliver of the surface, so a wheel over THEM must keep scrolling
		// that surface rather than falling through to whatever is behind. (The rest of the
		// bar needs no such help: it lets the wheel straight through to the surface itself.)
		pill.addEventListener("wheel", function (e) {
			p.el.scrollTop += e.deltaY;
			e.preventDefault();
			paint(p);
		}, { passive: false });
	}

	// ── attach / detach ─────────────────────────────────────────────────────────

	function attach(el) {
		if (!el || el.nodeType !== 1 || el.plScrollPager) { return null; }
		var p = { el: el, up: makeBar(-1), down: makeBar(1), mode: null, dir: 1, tops: [], movedAt: 0 };
		el.plScrollPager = p;
		p.up.plPager = p;
		p.down.plPager = p;
		bind(p, -1);
		bind(p, 1);
		document.body.appendChild(p.up);
		document.body.appendChild(p.down);
		// Movement itself un-veils: while the rows are travelling, the chevrons are what
		// explains the travelling, and they show over whatever they land on.
		el.addEventListener("scroll", function () {
			p.movedAt = performance.now();
			setVeil(p.up, false);
			setVeil(p.down, false);
			paint(p);
		}, { passive: true });
		pagers.push(p);
		startTracker();
		paint(p);
		return p;
	}

	function detach(p) {
		stopRun(p);
		if (p.up.parentNode) { p.up.parentNode.removeChild(p.up); }
		if (p.down.parentNode) { p.down.parentNode.removeChild(p.down); }
		if (p.el) { p.el.plScrollPager = null; }
		var i = pagers.indexOf(p);
		if (i >= 0) { pagers.splice(i, 1); }
	}

	// One tick does both jobs: pin/decide the bars, then decide which resting ones are in
	// the way. The veil pass runs HERE and nowhere on the hot paths — paint() is called on
	// every scroll event and on every frame of a held glide, and the ink test reads layout.
	function tick() {
		insetAll();
		paintAll();
		veilAll();
	}

	// Re-measure the sticky strip each surface's up bar has to clear. On the timer and not in
	// paint(), which also runs on every scroll event and on every frame of a held glide — and
	// caching costs nothing here, since a surface's headers are all the same height and the
	// answer only changes between "one is parked at the top" and "none is".
	function insetAll() {
		for (var i = 0; i < pagers.length; i++) {
			var p = pagers[i];
			if (!p.el.isConnected) { continue; }
			var box = contentBox(p.el);
			p.upInset = box ? stickyTopInset(p, box) : 0;
		}
	}

	function startTracker() {
		if (!tracker) { tracker = setInterval(tick, TRACK_MS); }
	}

	window.addEventListener("resize", paintAll);

	SCROLL_PAGER = {
		attach: attach,
		refresh: paintAll
	};

	$(function () {
		attach(document.getElementById("defBoxScrollId"));          // Dictionary / Practice
		attach(document.querySelector("#learnBoxId .defBoxScrollClass"));   // Learn
		attach(document.getElementById("wlAccordionId"));           // the word-list panel
		// An open list's own words (.wlWordsClass) are attached by main.js where the
		// section body mints that element — the only place it is created.
	});

})();
